import { useEffect, useMemo, useState } from "react";
import { Funnel, Eye } from "@phosphor-icons/react";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { toast } from "sonner";
import {
  DEMO_CHILDREN,
  DEMO_CLASSROOMS,
  listReportsForAdmin,
} from "@/lib/dailyReports";
import { isSupabaseConfigured, supabase } from "@/lib/supabase";
import { ENTRY_META, prettyDate } from "@/lib/entryTypes";
import EntryCard from "@/components/reports/EntryCard";
import { fetchReport } from "@/lib/dailyReports";
import { getChildById } from "@/lib/dailyReportsDemo";

export default function AdminDailyReports() {
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);

  const [classrooms, setClassrooms] = useState(DEMO_CLASSROOMS);
  const [childrenList, setChildrenList] = useState(DEMO_CHILDREN);

  const [filters, setFilters] = useState({
    classroomId: "",
    childId: "",
    dateFrom: "",
    dateTo: "",
  });

  const [detailOpen, setDetailOpen] = useState(false);
  const [detailReport, setDetailReport] = useState(null);
  const [detailChild, setDetailChild] = useState(null);

  // Load filter dropdown data in live mode (demo fixtures are already set).
  useEffect(() => {
    if (!isSupabaseConfigured) return;
    (async () => {
      const [cRes, kRes] = await Promise.all([
        supabase.from("classrooms").select("id, name").order("name"),
        supabase
          .from("children")
          .select("id, first_name, last_name, classroom_id")
          .order("first_name"),
      ]);
      if (cRes.data) setClassrooms(cRes.data);
      if (kRes.data) setChildrenList(kRes.data);
    })();
  }, []);

  // Load reports whenever filters change.
  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const rows = await listReportsForAdmin({
          classroomId: filters.classroomId || undefined,
          childId: filters.childId || undefined,
          dateFrom: filters.dateFrom || undefined,
          dateTo: filters.dateTo || undefined,
        });
        setReports(rows);
      } catch (err) {
        toast.error(err.message || "Could not load reports");
      } finally {
        setLoading(false);
      }
    })();
  }, [filters]);

  // Children narrowed by classroom filter for the dropdown.
  const filteredChildren = useMemo(() => {
    if (!filters.classroomId) return childrenList;
    return childrenList.filter((c) => c.classroom_id === filters.classroomId);
  }, [childrenList, filters.classroomId]);

  const openDetail = async (report) => {
    const child =
      report.child || getChildById(report.child_id) || childrenList.find((c) => c.id === report.child_id);
    setDetailChild(child);
    // Re-fetch the full report with entries for the detail drawer.
    try {
      const full = await fetchReport(report.child_id, report.report_date);
      setDetailReport(full || report);
    } catch {
      setDetailReport(report);
    }
    setDetailOpen(true);
  };

  return (
    <div data-testid="admin-daily-reports" className="space-y-8">
      <section>
        <p className="text-xs font-bold uppercase tracking-[0.15em] text-neutral-500">
          Operational
        </p>
        <h1 className="mt-1 font-heading text-3xl font-semibold tracking-tight text-neutral-900 sm:text-4xl">
          Daily reports
        </h1>
        <p className="mt-2 max-w-2xl text-neutral-600">
          Review reports submitted by teachers across all classrooms. Teacher
          attribution is preserved on every row.
        </p>
      </section>

      {/* Filters */}
      <section
        data-testid="admin-reports-filters"
        className="rounded-2xl border border-neutral-200 bg-white p-5"
      >
        <div className="mb-4 flex items-center gap-2">
          <Funnel size={18} weight="duotone" className="text-primary-hover" />
          <p className="text-xs font-bold uppercase tracking-[0.15em] text-neutral-500">
            Filters
          </p>
        </div>
        <div className="grid grid-cols-1 gap-3 md:grid-cols-4">
          <FilterSelect
            testId="filter-classroom"
            label="Classroom"
            value={filters.classroomId}
            onChange={(v) =>
              setFilters((f) => ({ ...f, classroomId: v, childId: "" }))
            }
            options={[
              { value: "", label: "All classrooms" },
              ...classrooms.map((c) => ({ value: c.id, label: c.name })),
            ]}
          />
          <FilterSelect
            testId="filter-child"
            label="Child"
            value={filters.childId}
            onChange={(v) => setFilters((f) => ({ ...f, childId: v }))}
            options={[
              { value: "", label: "All children" },
              ...filteredChildren.map((c) => ({
                value: c.id,
                label: `${c.first_name} ${c.last_name || ""}`.trim(),
              })),
            ]}
          />
          <FilterDate
            testId="filter-date-from"
            label="From"
            value={filters.dateFrom}
            onChange={(v) => setFilters((f) => ({ ...f, dateFrom: v }))}
          />
          <FilterDate
            testId="filter-date-to"
            label="To"
            value={filters.dateTo}
            onChange={(v) => setFilters((f) => ({ ...f, dateTo: v }))}
          />
        </div>
      </section>

      {/* Table */}
      <section className="rounded-2xl border border-neutral-200 bg-white overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center py-16">
            <div className="h-8 w-8 rounded-full border-2 border-primary/30 border-t-primary animate-spin" />
          </div>
        ) : reports.length === 0 ? (
          <div
            data-testid="admin-reports-empty"
            className="px-6 py-16 text-center"
          >
            <p className="font-heading text-base font-semibold text-neutral-900">
              No reports match these filters
            </p>
            <p className="mx-auto mt-2 max-w-sm text-sm text-neutral-500">
              Daily reports are written by teachers from the Roster page on
              their phones. They show up here for admin review automatically —
              no setup needed.
            </p>
          </div>
        ) : (
          <table
            data-testid="admin-reports-table"
            className="w-full border-collapse text-left text-sm"
          >
            <thead>
              <tr className="border-b border-neutral-200 bg-neutral-50">
                <Th>Date</Th>
                <Th>Child</Th>
                <Th>Classroom</Th>
                <Th>Entries</Th>
                <Th>Mood</Th>
                <Th className="text-right">Actions</Th>
              </tr>
            </thead>
            <tbody>
              {reports.map((r) => {
                const child =
                  r.child ||
                  getChildById(r.child_id) ||
                  childrenList.find((c) => c.id === r.child_id);
                const classroom =
                  r.classroom ||
                  classrooms.find((c) => c.id === r.classroom_id);
                return (
                  <tr
                    key={r.id}
                    data-testid={`admin-report-row-${r.id}`}
                    className="border-b border-neutral-100 transition-colors hover:bg-neutral-50"
                  >
                    <Td>{prettyDate(r.report_date)}</Td>
                    <Td className="font-medium text-neutral-900">
                      {child
                        ? `${child.first_name} ${child.last_name || ""}`.trim()
                        : "—"}
                    </Td>
                    <Td>{classroom?.name || "—"}</Td>
                    <Td>{r.entries?.length ?? 0}</Td>
                    <Td>
                      {r.overall_mood ? (
                        <span className="rounded-full bg-primary/10 px-2.5 py-0.5 text-xs font-semibold uppercase tracking-wider text-primary-hover">
                          {r.overall_mood}
                        </span>
                      ) : (
                        <span className="text-neutral-400">—</span>
                      )}
                    </Td>
                    <Td className="text-right">
                      <button
                        data-testid={`admin-report-view-${r.id}`}
                        onClick={() => openDetail(r)}
                        className="inline-flex items-center gap-1.5 rounded-full bg-neutral-100 px-3 py-1.5 text-xs font-medium text-neutral-800 hover:bg-neutral-200"
                      >
                        <Eye size={14} weight="duotone" />
                        View
                      </button>
                    </Td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </section>

      {/* Detail drawer */}
      <Sheet open={detailOpen} onOpenChange={setDetailOpen}>
        <SheetContent
          data-testid="admin-report-detail"
          className="w-full overflow-y-auto bg-background p-0 sm:max-w-lg"
        >
          <SheetHeader className="border-b border-neutral-200 bg-white px-6 py-5">
            <SheetTitle className="font-heading text-xl font-semibold tracking-tight text-neutral-900">
              {detailChild
                ? `${detailChild.first_name} ${detailChild.last_name || ""}`.trim()
                : "Report"}
            </SheetTitle>
            <p className="text-sm text-neutral-500">
              {detailReport ? prettyDate(detailReport.report_date) : ""}
            </p>
          </SheetHeader>
          <div className="space-y-4 p-6">
            {detailReport?.summary_note && (
              <p className="rounded-2xl border border-neutral-200 bg-white p-4 text-sm leading-relaxed text-neutral-800">
                {detailReport.summary_note}
              </p>
            )}
            {detailReport?.entries?.length ? (
              <ul className="space-y-2">
                {detailReport.entries.map((e) => (
                  <li key={e.id}>
                    <EntryCard entry={e} />
                  </li>
                ))}
              </ul>
            ) : (
              <p className="text-sm text-neutral-500">No entries on this report.</p>
            )}
            <p className="pt-2 text-xs text-neutral-400">
              Teacher ID: {detailReport?.created_by || "—"}
            </p>
          </div>
        </SheetContent>
      </Sheet>

      {reports.length > 0 && (
        <p className="text-xs text-neutral-500">
          Showing {reports.length} report{reports.length === 1 ? "" : "s"}.
          Aggregate counts by entry type:{" "}
          {Object.keys(ENTRY_META)
            .map(
              (t) =>
                `${ENTRY_META[t].label}: ${reports.reduce(
                  (acc, r) =>
                    acc +
                    (r.entries?.filter((e) => e.entry_type === t).length || 0),
                  0,
                )}`,
            )
            .join(" · ")}
        </p>
      )}
    </div>
  );
}

function Th({ children, className = "" }) {
  return (
    <th className={`px-4 py-3 text-xs font-bold uppercase tracking-[0.12em] text-neutral-500 ${className}`}>
      {children}
    </th>
  );
}

function Td({ children, className = "" }) {
  return (
    <td className={`px-4 py-3 text-sm text-neutral-700 ${className}`}>
      {children}
    </td>
  );
}

function FilterSelect({ label, value, onChange, options, testId }) {
  return (
    <label className="block">
      <span className="mb-1.5 block text-xs font-bold uppercase tracking-[0.15em] text-neutral-500">
        {label}
      </span>
      <select
        data-testid={testId}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="w-full rounded-xl border border-neutral-200 bg-white px-3 py-2.5 text-sm text-neutral-900 shadow-sm focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20"
      >
        {options.map((o) => (
          <option key={o.value} value={o.value}>
            {o.label}
          </option>
        ))}
      </select>
    </label>
  );
}

function FilterDate({ label, value, onChange, testId }) {
  return (
    <label className="block">
      <span className="mb-1.5 block text-xs font-bold uppercase tracking-[0.15em] text-neutral-500">
        {label}
      </span>
      <input
        data-testid={testId}
        type="date"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="w-full rounded-xl border border-neutral-200 bg-white px-3 py-2.5 text-sm text-neutral-900 shadow-sm focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20"
      />
    </label>
  );
}
